import { useDisclosure } from '@chakra-ui/react'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import AddCircleIcon from '../../../../../assets/icons/add-circle-stroke-rounded'
import AddBankAccountModal from '../../../../../components/AddBankAccountModal'
import InfoBox from '../../../../../components/InfoBox'
import PageWrapper from '../../../../../components/Pagewrapper'
import Table from '../../../../../components/Table/Table'
import { useGetBankAccountsQuery } from '../../../../../redux/services/coreApi'
import { setBankAccountsTableState } from '../../../../../redux/slices/tableFiltersSlice'
import { RootState } from '../../../../../redux/store'
import {
  BankAccountDetailsDto,
  BankAccountDto,
} from '../../../../../types/coreApi-types'
import EditBankAccountModal from './components/EditBankAccountModal'

type SystemBankAccountsScreenProps = {}

const SystemBankAccountsScreen: React.FC<
  SystemBankAccountsScreenProps
> = () => {
  const { data: bankAccounts, isLoading } = useGetBankAccountsQuery(null)
  const [selectedBankAccount, setSelectedBankAccount] =
    useState<BankAccountDto | null>(null)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const addBankAccountModal = useDisclosure()

  const bankAccountsTableState = useSelector(
    (state: RootState) => state.bankAccountsTable
  )

  const handleEditBankAccount = (bankAccount: BankAccountDto) => {
    setSelectedBankAccount(bankAccount)
    onOpen()
  }

  const handleCloseEditModal = () => {
    onClose()
    setSelectedBankAccount(null)
  }

  return (
    <PageWrapper title="Bank Accounts Overview">
      <Table
        tableState={bankAccountsTableState}
        setTableState={setBankAccountsTableState}
        navigate={() => null}
        isLoading={isLoading}
        onClickRow={(bankAccount: BankAccountDto) =>
          handleEditBankAccount(bankAccount)
        }
        emptyDataRender={
          <div className="text-xl mb-3">No bank accounts found</div>
        }
        defaultData={bankAccounts ?? []}
        schema={[
          {
            header: 'Name',
            accessorKey: 'name',
            type: 'string',
            priority: 1,
          },
          {
            header: 'Description',
            accessorKey: 'description',
            type: 'string',
            priority: 2,
            cellValueWrapper: value => (
              <div>{value || <span className="text-gray-400">-</span>}</div>
            ),
          },
          {
            header: 'Details Type',
            accessorKey: 'bankAccountDetails',
            type: 'string',
            priority: 3,
            cellValueWrapper: (value: any) => {
              // Determine if it's domestic or international
              const details = value as BankAccountDetailsDto
              const isDomestic = !!(
                details.registration || details.accountNumber
              )
              return (
                <div className="w-min flex flex-col">
                  <InfoBox colorScheme={isDomestic ? 'green' : 'gray'}>
                    {isDomestic ? 'Danish' : 'International'}
                  </InfoBox>
                </div>
              )
            },
          },
          {
            header: 'Account Number/IBAN',
            accessorKey: 'bankAccountDetails',
            type: 'string',
            priority: 4,
            cellValueWrapper: (value: any) => {
              const details = value as BankAccountDetailsDto
              const accountIdentifier = details.accountNumber || details.iban
              return (
                <div className="w-min">
                  {accountIdentifier ? (
                    <span>{accountIdentifier}</span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </div>
              )
            },
          },
          {
            header: 'Registration/BIC',
            accessorKey: 'bankAccountDetails',
            type: 'string',
            priority: 5,
            cellValueWrapper: (value: any) => {
              const details = value as BankAccountDetailsDto
              const regOrBic = details.registration || details.bic
              return (
                <div className="w-min">
                  {regOrBic ? (
                    <span>{regOrBic}</span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </div>
              )
            },
          },
        ]}
      />

      <div className="flex justify-end mt-6">
        <button
          className="button flex items-center gap-2 bg-primary text-white hover:bg-primary-dark transition-colors"
          onClick={addBankAccountModal.onOpen}
        >
          <AddCircleIcon width={20} height={20} color="white" />
          <span>Add Bank Account</span>
        </button>
      </div>

      <EditBankAccountModal
        isOpen={isOpen}
        onClose={handleCloseEditModal}
        bankAccount={selectedBankAccount}
      />
      <AddBankAccountModal
        isOpen={addBankAccountModal.isOpen}
        onClose={addBankAccountModal.onClose}
        systemBankAccount={true}
      />
    </PageWrapper>
  )
}

export default SystemBankAccountsScreen
